import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function ReportPage() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [uniqueCounts, setUniqueCounts] = useState(null);
  const [error, setError] = useState("");
  
  // UTV member states
  const [isUTVMember, setIsUTVMember] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Location and company states
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Verify token and get user data
  const verifyTokenWithAuth = useCallback(async (authToken) => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data?.error?.name === "TokenExpiredError") {
        localStorage.removeItem("superadmintoken");
        return false;
      }

      if (response.data?.success && response.data?.user) {
        const userData = response.data.user;
        setIsUTVMember(userData.UTVMember || false);
        
        // Set location and company from API response for non-UTV members
        if (userData.CompanyLocation) {
          setSelectedLocation(userData.CompanyLocation);
          setSelectedLocationId(userData.currentLocationId);
        }
        if (userData.MyCompany) {
          setSelectedCompany(userData.MyCompany);
        }

        console.log("ReportPage - User data loaded:", {
          UTVMember: userData.UTVMember,
          location: userData.CompanyLocation,
          company: userData.MyCompany
        });

        return true;
      }
      return false;
    } catch (error) {
      console.error("Token verification failed:", error);
      localStorage.removeItem("superadmintoken");
      return false;
    }
  }, [BASE_URL]);

  // Initialize app
  const initializeApp = useCallback(async () => {
    try {
      setIsInitializing(true);
      
      // Check token
      const token = localStorage.getItem("superadmintoken");
      if (!token) {
        window.location.href = "/lkart/superadmin/";
        return;
      }

      // Verify token
      const isTokenValid = await verifyTokenWithAuth(token);
      if (!isTokenValid) {
        window.location.href = "/lkart/superadmin/";
        return;
      }

      // Fetch locations for UTV members
      if (isUTVMember) {
        await fetchLocations();
      }

      setIsInitializing(false);
    } catch (error) {
      console.error("Initialization error:", error);
      setIsInitializing(false);
    }
  }, [verifyTokenWithAuth, isUTVMember]);

  // Fetch locations for UTV members
  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.get(`${BASE_URL}/lenskart/locations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success && Array.isArray(response.data.locations)) {
        setLocations(response.data.locations);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error fetching locations:", error);
      return false;
    }
  };

  // Fetch companies based on selected location
  const fetchCompanies = async (selectedLocationId) => {
    if (!selectedLocation) {
      setCompanies([]);
      setSelectedCompany("");
      return;
    }

    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem("superadmintoken");
      const response = await axios.get(
        `${BASE_URL}/onDemandCompanies/list-companies/${selectedLocationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && Array.isArray(response.data.companies)) {
        setCompanies(response.data.companies);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Fetch report data
  const fetchAllTimeReportData = useCallback(async () => {
    // For UTV members: need both location and company selected
    // For non-UTV members: company is pre-selected from API
    const shouldFetch = isUTVMember 
      ? (selectedLocation && selectedCompany)
      : selectedCompany;

    if (!shouldFetch) {
      setReportData([]);
      setUniqueCounts(null);
      setError("");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      // Prepare request body
      const requestBody = {};
      
      // Add companyId if available
      if (selectedCompany) {
        requestBody.companyId = selectedCompany;
      }
      
      // Add location if it's a UTV member
      if (isUTVMember && selectedLocation) {
        requestBody.location = selectedLocation;
        requestBody.locationId=selectedLocationId;
      }

      const token = localStorage.getItem("superadmintoken");
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/shift-wise-summary`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Set report data and unique counts from response
      setReportData(response.data.data || []);
      setUniqueCounts(response.data.uniqueCounts || null);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
      setUniqueCounts(null);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, selectedCompany, isUTVMember, BASE_URL]);

  // Initialize on component mount
  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  // Fetch companies when location changes (for UTV members)
  useEffect(() => {
    if(selectedLocationId)
    {
      fetchCompanies(selectedLocationId);

    }

  }, [selectedLocation, isUTVMember]);

  // Reset company when location changes (for UTV members)
  useEffect(() => {
    if (isUTVMember) {
      setSelectedCompany("");
      setReportData([]);
      setUniqueCounts(null);
    }
  }, [selectedLocation, isUTVMember]);

  // Fetch report data when company changes
  useEffect(() => {
    fetchAllTimeReportData();
  }, [selectedCompany, fetchAllTimeReportData]);

  // Calculate totals from reportData
  const calculateTotals = () => {
    if (reportData.length === 0) return { totalPoolSize: 0, totalShifts: 0 };

    const totals = reportData.reduce(
      (acc, item) => {
        acc.totalPoolSize += item.poolSize || 0;
        acc.totalShifts += item.totalShifts || 0;
        return acc;
      },
      { totalPoolSize: 0, totalShifts: 0 }
    );

    return totals;
  };

  const totals = calculateTotals();

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 pt-24">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
          <p className="text-gray-600">Loading report page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen pt-24">
      {/* Location and Company Dropdowns */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Location Field */}
          <div className="flex-1 relative">
            <label className="text-sm text-gray-600 mb-1 block">
              Location {isUTVMember && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {/* For UTV Members: Interactive Dropdown */}
              {isUTVMember ? (
                <>
                  <button
                    onClick={() => setIsLocationOpen(!isLocationOpen)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white"
                  >
                    {selectedLocation || "Select Location"}
                    <svg
                      className={`h-4 w-4 transform transition-transform ${
                        isLocationOpen ? "rotate-180" : ""
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {isLocationOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {locations.map((location, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setSelectedLocation(location.name);
                            setSelectedLocationId(location._id);
                            setIsLocationOpen(false);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${
                            selectedLocation === location.name
                              ? "bg-indigo-50 text-indigo-700"
                              : "text-gray-700"
                          }`}
                        >
                          {location.name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* For Non-UTV Members: Read-only Input */
                <input
                  type="text"
                  readOnly
                  value={selectedLocation}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                />
              )}
            </div>
          </div>

          {/* Company Field */}
          <div className="flex-1 relative">
            <label className="text-sm text-gray-600 mb-1 block">
              Company {isUTVMember && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {/* For UTV Members: Interactive Dropdown */}
              {isUTVMember ? (
                <>
                  <button
                    onClick={() => {
                      if (selectedLocation && !loadingCompanies) {
                        setIsCompanyOpen(!isCompanyOpen);
                      }
                    }}
                    disabled={!selectedLocation || loadingCompanies}
                    className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center ${
                      !selectedLocation || loadingCompanies
                        ? "bg-gray-100 cursor-not-allowed text-gray-400"
                        : "bg-white"
                    }`}
                  >
                    {loadingCompanies ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-2"></div>
                        Loading...
                      </span>
                    ) : selectedCompany ? (
                      companies.find((company) => company._id === selectedCompany)
                        ?.name || "Select Company"
                    ) : (
                      "Select Company"
                    )}
                    <svg
                      className={`h-4 w-4 transform transition-transform ${
                        isCompanyOpen ? "rotate-180" : ""
                      } ${
                        !selectedLocation || loadingCompanies ? "text-gray-400" : ""
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {isCompanyOpen && selectedLocation && !loadingCompanies && (
                    <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
                      {companies.length > 0 ? (
                        companies.map((company) => (
                          <button
                            key={company._id}
                            onClick={() => {
                              setSelectedCompany(company._id);
                              setIsCompanyOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${
                              selectedCompany === company._id
                                ? "bg-indigo-50 text-indigo-700"
                                : "text-gray-700"
                            }`}
                          >
                            {company.name}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          No companies found for this location
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* For Non-UTV Members: Read-only Input */
                <input
                  type="text"
                  readOnly
                  value= {companies.find(c => c._id === selectedCompany)?.name || selectedCompanyName || selectedCompany}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed"
                />
              )}
            </div>
          </div>
        </div>

        {/* Validation message */}
        {isUTVMember && !selectedCompany && selectedLocation && (
          <div className="text-blue-600 text-sm mb-2">
            Please select a company to view the report
          </div>
        )}
      </div>

      {/* Report header */}
      <div className="mb-4 bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <h2 className="text-xl font-bold mb-2 sm:mb-0">All Time Report</h2>
          <div className="text-sm text-gray-600">
            {selectedCompany ? (
              isUTVMember ? (
                <>
                  <span>Location: <span className="font-medium">{selectedLocation}</span></span>
                  <span className="mx-2">•</span>
                  <span>Company: <span className="font-medium">
                    {companies.find(c => c._id === selectedCompany)?.name || selectedCompany}
                  </span></span>
                </>
              ) : (
                <span>Company: <span className="font-medium">
                  {companies.find(c => c._id === selectedCompany)?.name || selectedCompanyName || selectedCompany}
                </span></span>
              )
            ) : (
              <span className="text-gray-400">Select location and company to view report</span>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
            <p className="text-gray-600">Loading report data...</p>
          </div>
        </div>
      )}

      {/* Report Table - Only show when we have data or loading is complete */}
      {!loading && selectedCompany && (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg py-4 mb-6">
          <table className="w-full border-collapse">
            <thead>
              {/* First header row - lighter grey */}
              <tr className="bg-gray-200 text-black text-center border border-black">
                <th rowSpan="2" className="py-3 px-2 border border-black">
                  S.No
                </th>
                <th rowSpan="2" className="py-3 px-2 border border-black">
                  Department
                </th>
                <th
                  rowSpan="2"
                  className="py-3 px-2 border border-black min-w-[200px]"
                >
                  Skills
                </th>
                <th rowSpan="2" className="py-3 px-2 border border-black">
                  Pool Size
                </th>
                <th
                  colSpan="6"
                  className="py-3 px-2 border border-black font-semibold"
                >
                  Shift Count
                </th>
                <th
                  rowSpan="2"
                  className="py-3 px-2 border border-black font-semibold"
                >
                  Total Shifts
                </th>
              </tr>

              {/* Second header row - slightly darker grey */}
              <tr className="bg-gray-300 text-black text-center border border-black">
                <th className="border border-black py-2 px-2">1</th>
                <th className="border border-black py-2 px-2">2–5</th>
                <th className="border border-black py-2 px-2">6-8</th>
                <th className="border border-black py-2 px-2">9-12</th>
                <th className="border border-black py-2 px-2">13-15</th>
                <th className="border border-black py-2 px-2">{">"}15</th>
              </tr>
            </thead>

            <tbody>
              {reportData.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center py-6 text-gray-500">
                    No data available for selected filters
                  </td>
                </tr>
              ) : (
                <>
                  {reportData.map((row, i) => (
                    <tr
                      key={row.serialNo || i}
                      className="text-center hover:bg-gray-100 transition"
                    >
                      <td className="border p-3">{row.serialNo || i + 1}</td>
                      <td className="border p-3 font-medium">
                        {row.departmentName}
                      </td>
                      <td className="border p-3 text-left break-words">
                        {row.skillsDescription}
                      </td>
                      <td className="border p-3">{row.poolSize}</td>
                      <td className="border p-3">
                        {row.shiftBuckets?.["1"] || 0}
                      </td>
                      <td className="border p-3">
                        {row.shiftBuckets?.["2-5"] || 0}
                      </td>
                      <td className="border p-3">
                        {row.shiftBuckets?.["6-8"] || 0}
                      </td>
                      <td className="border p-3">
                        {row.shiftBuckets?.["9-12"] || 0}
                      </td>
                      <td className="border p-3">
                        {row.shiftBuckets?.["13-15"] || 0}
                      </td>
                      <td className="border p-3">
                        {row.shiftBuckets?.[">15"] || 0}
                      </td>
                      <td className="border p-3 font-bold">
                        {row.totalShifts || 0}
                      </td>
                    </tr>
                  ))}

                  {/* Totals Row */}
                  {reportData.length > 0 && (
                    <tr className="text-center bg-gray-50 font-semibold">
                      <td className="border p-3" colSpan="3">
                        Total (overall)
                      </td>
                      <td className="border p-3">{totals.totalPoolSize}</td>
                      <td className="border p-3">
                        {reportData.reduce(
                          (sum, item) => sum + (item.shiftBuckets?.["1"] || 0),
                          0
                        )}
                      </td>
                      <td className="border p-3">
                        {reportData.reduce(
                          (sum, item) => sum + (item.shiftBuckets?.["2-5"] || 0),
                          0
                        )}
                      </td>
                      <td className="border p-3">
                        {reportData.reduce(
                          (sum, item) => sum + (item.shiftBuckets?.["6-8"] || 0),
                          0
                        )}
                      </td>
                      <td className="border p-3">
                        {reportData.reduce(
                          (sum, item) =>
                            sum + (item.shiftBuckets?.["9-12"] || 0),
                          0
                        )}
                      </td>
                      <td className="border p-3">
                        {reportData.reduce(
                          (sum, item) =>
                            sum + (item.shiftBuckets?.["13-15"] || 0),
                          0
                        )}
                      </td>
                      <td className="border p-3">
                        {reportData.reduce(
                          (sum, item) => sum + (item.shiftBuckets?.[">15"] || 0),
                          0
                        )}
                      </td>
                      <td className="border p-3">{totals.totalShifts}</td>
                    </tr>
                  )}

                  {/* Unique Workers Summary Row */}
                  {uniqueCounts && (
                    <tr className="text-center bg-blue-50 font-semibold">
                      <td className="border p-3" colSpan="3">
                        Unique Workers Summary
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.totalUniqueWorkers}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.uniqueWorkersByShiftBucket?.["1"] ||
                          0}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.uniqueWorkersByShiftBucket?.[
                          "2-5"
                        ] || 0}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.uniqueWorkersByShiftBucket?.[
                          "6-8"
                        ] || 0}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.uniqueWorkersByShiftBucket?.[
                          "9-12"
                        ] || 0}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.uniqueWorkersByShiftBucket?.[
                          "13-15"
                        ] || 0}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.uniqueWorkersByShiftBucket?.[
                          ">15"
                        ] || 0}
                      </td>
                      <td className="border p-3 text-blue-700">
                        {uniqueCounts.overall.totalUniqueShifts}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State - When no company is selected */}
      {!loading && !selectedCompany && !isInitializing && (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow">
          <div className="text-center">
            <svg className="h-16 w-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Report Data</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {isUTVMember 
                ? "Select a location and company to view the report data"
                : "Company information is loading..."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}